import { ME } from '../../3-store/core/me';
import { isOnline, peers, type Peer } from '../../3-store/core/peers';
import { setGroup, wantMedia, type SfuWant } from '../../3-store/meeting/links';
import { NEAR_MAX, SCHOOL_GROUP, canLink, isNear, linkDist, linkGroup, type LinkGroup, type Located } from '../../../shared/world/links';
import { SFU_KINDS } from '../../../shared/protocol';
import type { PlaceId } from '../../../shared/ids';
import { myKinds } from './classroom';

// 영상·음성 연결 규칙(사용자가 정했다). 누구와 이어지는가는 shared/world/links.ts의 규칙 하나이고 서버가 같은 규칙으로 신호 전달·SFU 입장을 검사한다.
//   학교: 안에 있는 모두(앉았든 서 있든) · 테이블·광장 벤치: 같은 테이블에 앉은 사람만(앉은 사람은 지나가는 사람과 이어지지 않는다)
//   그 밖에 서 있는 사람: 같은 장소에서 가까운 사람(NEAR_PX 안에서 시작, FAR_PX까지 유지, 가까운 순 NEAR_MAX명). 쌍마다 계산하니 이어서 건너지 않는다
export const linkGroupOf = (p: { room: PlaceId | null; seat: number }): LinkGroup => linkGroup(p.room, p.seat);

const DIRECT_MAX = 10;   // 학교에서 직접 연결을 맺는 사람 수 상한(나머지는 서버 송출로 받는다). 테이블 모임은 여섯이 최대라 상한에 걸리지 않는다. ponytail: 상한을 넘으면 앞에서부터 자른다(v2는 가까운 순). 서버 송출이 안 되는 곳에서 열 명을 넘는 학교는 일부가 안 이어진다
let linkT = 0, linked = new Set<string>();   // linked: 지금 이어져 있는 쌍(멀어질 때 더 오래 유지하려고 기억한다)
// 이어질 상대의 접속 id. was = 지금 이어져 있는 id들
export function linkTargets(me: Located, others: Iterable<Peer>, was: ReadonlySet<string> = new Set()): string[] {
  const g = linkGroupOf(me);
  if (g === null) return [];
  const ok = [...others].filter((r) => canLink(me, r, was.has(r.id)));
  if (g === SCHOOL_GROUP) return ok.slice(0, DIRECT_MAX).map((r) => r.id);
  if (isNear(g)) return ok.sort((a, b) => linkDist(a, me) - linkDist(b, me)).slice(0, NEAR_MAX).map((r) => r.id);   // 가까운 순
  return ok.map((r) => r.id);
}

// 서버 송출(SFU)이 원하는 상태: 학교 모임이면 켜고(올릴 수 있는 종류는 myKinds: 강사가 있으면 강사·발언권만, 받을 것은 남이 공지한 트랙 전부), 아니면 끈다
export function wantSfu(): SfuWant {
  if (!isOnline() || linkGroupOf(ME) !== SCHOOL_GROUP) return { on: false };
  const recv: { id: string; kind: (typeof SFU_KINDS)[number]; name: string }[] = [];
  for (const r of peers.values()) if (linkGroupOf(r) === SCHOOL_GROUP && r.pub) for (const k of SFU_KINDS) if (r.pub[k]) recv.push({ id: r.id, kind: k, name: r.pub[k]! });
  return { on: true, may: myKinds(), recv };
}
// 보내기 정책: 서버 송출로 올리고 있고 그 사람이 서버 송출로 받는 중이면 직접 연결로는 보내지 않는다. sfuSending = 내 kind를 서버로 올리는 중인가(영상 쪽이 아는 값이라 부르는 쪽이 넘긴다)
export function sendPolicy(id: string, sfuSending: boolean) {
  const r = peers.get(id);
  return !(r && r.pub && r.pub.rx && sfuSending && linkGroupOf(ME) === SCHOOL_GROUP);
}

// 0.25초마다 원하는 상태를 다시 정한다. force = 장소·자리가 바뀐 바로 그 프레임
export function updateLinks(dt: number, force = false) {
  if (!force && (linkT -= dt) > 0) return;
  linkT = 0.25;
  const ids = isOnline() ? linkTargets(ME, peers.values(), linked) : [];
  linked = new Set(ids);
  setGroup(isOnline() ? linkGroupOf(ME) : null, ids.length + 1);
  wantMedia(ids, wantSfu());
}
